/**
 * Tapestry packer — measure-then-place layout for the Tapestry view.
 *
 * Approach
 * --------
 * 1. For each visible symbol, pick its sentence + font size + target line
 *    width (varied per role and seeded per symbol). Measure the actual text
 *    via DOM-based measureText so widths reflect the font the text is set in
 *    (`fontFamily`: the skin's face — Hand1-Regular on the chalkboard, the
 *    scene's caps face on paper; measuring in one and setting in the other
 *    left every line to the render-time squeeze, TT-1174 note 1).
 * 2. Wrap the sentence into lines using greedy fill against the target width;
 *    that yields the actual longest-line-width and total-height of the text
 *    block — i.e. a real measured footprint.
 * 3. Place each item one at a time: try a deterministic shuffled grid of
 *    candidate positions, test BOTH sides of the symbol for the curve, and
 *    accept the first non-colliding placement. Items are tested via real AABB
 *    overlap against every previously-placed symbol AND text block.
 *
 * Determinism
 * -----------
 * Pure function of (visible symbols, their firstIdx, sentences). All
 * randomness is seeded by symbol_id+firstIdx. Same inputs → same output.
 *
 * Coordinate space: y ∈ [0, 1], x ∈ [0, ASPECT]. Pixel measurements get
 * divided by canvasHeight to convert to height-normalized units.
 */

import { measureText, MIN_FONT_SIZE } from './tapestryText.js';
import { makeRng, seedFor, randRange } from './tapestryGenerative.js';
import { textMatchesKeywords } from './symbolMeta.js';

export const CANVAS_ASPECT = 16 / 9;

const ROLE_SIZE = {
  primary: 0.4,
  secondary: 0.2,
  tertiary: 0.13,
};

const ROLE_FONT_BASE = {
  primary: 46,
  secondary: 32,
  tertiary: 24,
};

const ROLE_MAX_LINES = {
  primary: 5,
  secondary: 4,
  tertiary: 3,
};

// Per-role base curve width as a fraction of canvas height. The actual
// per-symbol target is jittered within ±BASE * VARIANCE for variety.
const ROLE_TARGET_WIDTH = {
  primary: { base: 0.55, variance: 0.18 },
  secondary: { base: 0.4, variance: 0.16 },
  tertiary: { base: 0.28, variance: 0.1 },
};

const SYMBOL_TEXT_GAP = 0.018;
const EDGE_PAD = 0.04;
const PRIMARY_JITTER = 0.06;
const PRIMARY_ATTEMPTS = 24;
const CANDIDATE_GRID_COLS = 9;
const CANDIDATE_GRID_ROWS = 7;
const LINE_HEIGHT_RATIO = 1.05;

/**
 * Top-level entrypoint. `state` is from useTapestryState; `canvasHeight`
 * is the rendered pixel height of the canvas. `prevPlacements`, if
 * provided, is a `Map<symbolId, { x, y, side }>` of the previous frame's
 * placements — the placer prefers to keep each symbol near its old spot
 * rather than redoing first-fit from scratch, which is what produced
 * the dramatic cross-canvas jumps when other symbols crowded a slot.
 * Returns an array of placements ready to render.
 */
export function packLayout(state, canvasHeight, keywordsMap = new Map(), prevPlacements = null, fontFamily, measure = measureText) {
  if (!state || !state.symbols || !canvasHeight) return [];

  const visible = collectVisible(state);

  // Measure + place sequentially so that each symbol's chosen sentence
  // can be excluded from the pool of candidates for subsequent symbols.
  // Avoids the same sentence appearing on two external textpaths.
  const usedSentences = new Set();
  const placed = [];
  for (const v of visible) {
    const kw = keywordsMap.get(v.symbolId) || null;
    const measured = measureItem(v, canvasHeight, usedSentences, kw, fontFamily, measure);
    if (measured.sentence) usedSentences.add(measured.sentence);
    const prev = prevPlacements ? prevPlacements.get(measured.symbolId) || null : null;
    const placement = placeItem(measured, placed, prev);
    if (placement) placed.push(placement);
  }

  return placed.map((p) => buildOutput(p, canvasHeight));
}

// ---------- Collection ordering ----------

function collectVisible(state) {
  const items = Object.entries(state.symbols)
    .filter(([, e]) => e.role !== 'hidden' && ROLE_SIZE[e.role])
    .map(([symbolId, entry]) => ({ symbolId, entry }));

  const primaryIdx = items.findIndex((i) => i.entry.role === 'primary');
  const ordered = [];
  if (primaryIdx >= 0) ordered.push(items[primaryIdx]);
  ordered.push(...items.filter((_, i) => i !== primaryIdx).sort((a, b) => a.entry.firstIdx - b.entry.firstIdx));
  return ordered;
}

// ---------- Measurement ----------

function pickFontSize(role, sentence) {
  const base = ROLE_FONT_BASE[role];
  const wordCount = sentence.trim().split(/\s+/).length;
  // Slightly smaller for very long sentences
  const t = Math.max(0, Math.min(1, (wordCount - 6) / 24));
  return base * (1 - t * 0.25);
}

function pickTargetWidth(role, rng) {
  const cfg = ROLE_TARGET_WIDTH[role];
  return cfg.base + randRange(rng, -cfg.variance, cfg.variance);
}

/**
 * Wrap words into lines greedily so each line stays under targetWidthPx.
 * Returns measured layout: lines (arrays of words), per-line widths, total
 * height, max line width (becomes the curve length).
 */
export function layoutText(sentence, fontSize, targetWidthPx, fontFamily, measure = measureText) {
  const words = sentence.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;

  // Per-word widths including trailing space (except last)
  const wordWidths = words.map((w, i) => measure(w + (i < words.length - 1 ? ' ' : ''), fontSize, fontFamily).width);
  const longest = Math.max(...wordWidths);
  // Effective target must accommodate the longest word
  const effective = Math.max(targetWidthPx, longest * 1.05);

  const lines = [[]];
  const lineWidths = [0];
  for (let i = 0; i < words.length; i++) {
    const last = lineWidths.length - 1;
    if (lineWidths[last] + wordWidths[i] > effective && lines[last].length > 0) {
      lines.push([]);
      lineWidths.push(0);
    }
    lines[lines.length - 1].push(words[i]);
    lineWidths[lineWidths.length - 1] += wordWidths[i];
  }

  const maxLineWidth = Math.max(...lineWidths);
  const lineHeightPx = fontSize * LINE_HEIGHT_RATIO;
  return {
    lines,
    lineCount: lines.length,
    maxLineWidth,
    totalHeight: lines.length * lineHeightPx,
    lineHeightPx,
  };
}

function measureItem(item, canvasHeight, usedSentences, keywords, fontFamily, measure) {
  const role = item.entry.role;
  const sizeUnits = ROLE_SIZE[role];

  // Sort by salience score descending and drop anything already displayed
  // on another symbol's external textpath this frame. If the caller
  // supplied a keyword set for this symbol, also require the sentence to
  // actually contain one of the symbol's library keywords — the enriched
  // semantic match isn't always enough to feel on-topic.
  const sorted = (item.entry.sentences || [])
    .filter((s) => {
      if (!s || !s.text) return false;
      if (usedSentences.has(s.text)) return false;
      if (keywords && keywords.size > 0 && !textMatchesKeywords(s.text, keywords)) return false;
      return true;
    })
    .slice()
    .sort((a, b) => (b.effectiveScore ?? b.score ?? 0) - (a.effectiveScore ?? a.score ?? 0))
    .map((s) => s.text);

  if (sorted.length === 0) {
    return { ...item, role, sizeUnits, hasText: false };
  }

  const seed = seedFor(item.symbolId, item.entry.firstIdx + 13);
  const rng = makeRng(seed);
  const targetWidthPx = pickTargetWidth(role, rng) * canvasHeight;
  const maxLines = ROLE_MAX_LINES[role];

  // Try each sentence in score order until one lays out within maxLines
  for (const sentence of sorted) {
    let fontSize = pickFontSize(role, sentence);
    let layout = layoutText(sentence, fontSize, targetWidthPx, fontFamily, measure);
    while (layout && layout.lineCount > maxLines && fontSize > MIN_FONT_SIZE) {
      fontSize *= 0.9;
      layout = layoutText(sentence, fontSize, targetWidthPx, fontFamily, measure);
    }
    if (layout && layout.lineCount <= maxLines) {
      // Pad the text width by a few percent so the curve (and collision
      // footprint) leave a sliver of extra room for any small rendering
      // vs measurement drift. Prevents the last glyph from clipping.
      const paddedWidthPx = layout.maxLineWidth * 1.04;
      return {
        ...item,
        role,
        sizeUnits,
        hasText: true,
        sentence,
        fontSize,
        layout,
        textWidthUnits: paddedWidthPx / canvasHeight,
        textHeightUnits: layout.totalHeight / canvasHeight,
      };
    }
  }

  // No remaining sentence can be laid out cleanly — render symbol without text
  return { ...item, role, sizeUnits, hasText: false };
}

// ---------- Footprint construction ----------

function makeBbox(x1, y1, x2, y2) {
  return { x1, y1, x2, y2 };
}

function bboxesOverlap(a, b) {
  if (!a || !b) return false;
  return !(a.x2 <= b.x1 || b.x2 <= a.x1 || a.y2 <= b.y1 || b.y2 <= a.y1);
}

function fitsInBounds(bbox) {
  return bbox.x1 >= EDGE_PAD && bbox.x2 <= CANVAS_ASPECT - EDGE_PAD && bbox.y1 >= EDGE_PAD && bbox.y2 <= 1 - EDGE_PAD;
}

function symbolBbox(x, y, item) {
  const half = item.sizeUnits / 2;
  return makeBbox(x - half, y - half, x + half, y + half);
}

function textBbox(x, y, item, side) {
  if (!item.hasText) return null;
  const half = item.sizeUnits / 2;
  const tw = item.textWidthUnits;
  const th = item.textHeightUnits;
  // Center text vertically on the symbol
  const y1 = y - th / 2;
  const y2 = y + th / 2;
  if (side >= 0) {
    return makeBbox(x + half + SYMBOL_TEXT_GAP, y1, x + half + SYMBOL_TEXT_GAP + tw, y2);
  }
  return makeBbox(x - half - SYMBOL_TEXT_GAP - tw, y1, x - half - SYMBOL_TEXT_GAP, y2);
}

function combinedBbox(sym, text) {
  if (!text) return sym;
  return makeBbox(
    Math.min(sym.x1, text.x1),
    Math.min(sym.y1, text.y1),
    Math.max(sym.x2, text.x2),
    Math.max(sym.y2, text.y2)
  );
}

function computeFootprint(x, y, item, side) {
  const sym = symbolBbox(x, y, item);
  const text = textBbox(x, y, item, side);
  return { sym, text, combined: combinedBbox(sym, text) };
}

function collidesWithAny(fp, placed) {
  for (const p of placed) {
    if (!bboxesOverlap(fp.combined, p.footprint.combined)) continue;
    // Detailed overlap test — sym↔sym, sym↔text, text↔text
    if (bboxesOverlap(fp.sym, p.footprint.sym)) return true;
    if (fp.text && bboxesOverlap(fp.text, p.footprint.sym)) return true;
    if (p.footprint.text && bboxesOverlap(fp.sym, p.footprint.text)) return true;
    if (fp.text && p.footprint.text && bboxesOverlap(fp.text, p.footprint.text)) return true;
  }
  return false;
}

// ---------- Placement ----------

/**
 * Generate a deterministic shuffled set of candidate positions across the
 * canvas, stratified into a grid so coverage is even but seeded per-symbol
 * for variety.
 */
function generateCandidates(rng) {
  const cells = [];
  for (let gy = 0; gy < CANDIDATE_GRID_ROWS; gy++) {
    for (let gx = 0; gx < CANDIDATE_GRID_COLS; gx++) {
      cells.push({
        x: ((gx + 0.5) / CANDIDATE_GRID_COLS) * CANVAS_ASPECT + randRange(rng, -0.04, 0.04),
        y: (gy + 0.5) / CANDIDATE_GRID_ROWS + randRange(rng, -0.04, 0.04),
      });
    }
  }
  // Fisher-Yates shuffle
  for (let i = cells.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = cells[i];
    cells[i] = cells[j];
    cells[j] = tmp;
  }
  return cells;
}

function placePrimary(item, placed) {
  const seed = seedFor(item.symbolId, item.entry.firstIdx);
  const rng = makeRng(seed);
  const baseX = CANVAS_ASPECT / 2;
  const baseY = 0.5;

  for (let attempt = 0; attempt < PRIMARY_ATTEMPTS; attempt++) {
    const x = baseX + randRange(rng, -PRIMARY_JITTER, PRIMARY_JITTER);
    const y = baseY + randRange(rng, -PRIMARY_JITTER, PRIMARY_JITTER);
    for (const side of [+1, -1]) {
      const fp = computeFootprint(x, y, item, side);
      if (fitsInBounds(fp.combined) && !collidesWithAny(fp, placed)) {
        return { ...item, x, y, side, footprint: fp };
      }
    }
  }
  // Fallback: dead center, accept overlap
  const fp = computeFootprint(baseX, baseY, item, +1);
  return { ...item, x: baseX, y: baseY, side: +1, footprint: fp };
}

function placeOther(item, placed, prev) {
  const seed = seedFor(item.symbolId, item.entry.firstIdx + 7);
  const rng = makeRng(seed);
  const candidates = generateCandidates(rng);

  // Stickiness: if this symbol had a previous position, try it first
  // (preferring its previous text side), then bias the candidate order
  // toward proximity so any required relocation is gentle rather than
  // a wild jump across the canvas.
  if (prev) {
    for (const side of [prev.side, -prev.side]) {
      const fp = computeFootprint(prev.x, prev.y, item, side);
      if (fitsInBounds(fp.combined) && !collidesWithAny(fp, placed)) {
        return { ...item, x: prev.x, y: prev.y, side, footprint: fp };
      }
    }
    candidates.sort((a, b) => {
      const da = (a.x - prev.x) ** 2 + (a.y - prev.y) ** 2;
      const db = (b.x - prev.x) ** 2 + (b.y - prev.y) ** 2;
      return da - db;
    });
  }

  for (const cand of candidates) {
    for (const side of [+1, -1]) {
      const fp = computeFootprint(cand.x, cand.y, item, side);
      if (fitsInBounds(fp.combined) && !collidesWithAny(fp, placed)) {
        return { ...item, x: cand.x, y: cand.y, side, footprint: fp };
      }
    }
  }
  // Couldn't fit cleanly — try without text (just the symbol)
  if (item.hasText) {
    const noText = { ...item, hasText: false };
    for (const cand of candidates) {
      const fp = computeFootprint(cand.x, cand.y, noText, +1);
      if (fitsInBounds(fp.combined) && !collidesWithAny(fp, placed)) {
        return { ...noText, x: cand.x, y: cand.y, side: +1, footprint: fp };
      }
    }
  }
  return null; // skip — too crowded for any spot
}

function placeItem(item, placed, prev) {
  if (item.role === 'primary') return placePrimary(item, placed);
  return placeOther(item, placed, prev);
}

// ---------- Output ----------

/**
 * Convert the internal placement record to the shape the renderers consume:
 * adds the actual SVG curve geometry (path d, start/end/ctrl in normalized
 * units) so TapestryExternal can just draw the precomputed text without
 * re-measuring anything.
 */
function buildOutput(p, canvasHeight) {
  const seed = seedFor(p.symbolId, p.entry.firstIdx + 21);
  const rng = makeRng(seed);
  const tilt = randRange(rng, -0.012, 0.012);
  const bow = randRange(rng, -0.01, 0.01);

  let curve = null;
  if (p.hasText && p.layout) {
    const halfSym = p.sizeUnits / 2;
    // Use the padded textWidthUnits that was also used for collision, so
    // the curve extent matches what the placer reserved.
    const widthUnits = p.textWidthUnits;
    const lineHeightUnits = p.layout.lineHeightPx / canvasHeight;

    // Path goes left-to-right always, anchored to the chosen side of the symbol
    let sx, ex;
    if (p.side >= 0) {
      sx = p.x + halfSym + SYMBOL_TEXT_GAP;
      ex = sx + widthUnits;
    } else {
      ex = p.x - halfSym - SYMBOL_TEXT_GAP;
      sx = ex - widthUnits;
    }
    // Center the multi-line text block vertically on the symbol
    // First line's baseline approx top of text block + half a line
    const topOfText = p.y - p.textHeightUnits / 2;
    const sy = topOfText + lineHeightUnits * 0.5;
    const ey = sy + tilt;
    const midX = (sx + ex) / 2;
    const midY = (sy + ey) / 2 + bow;
    curve = {
      start: { x: sx, y: sy },
      end: { x: ex, y: ey },
      ctrl: { x: midX, y: midY },
    };
  }

  return {
    symbolId: p.symbolId,
    role: p.role,
    entry: p.entry,
    x: p.x,
    y: p.y,
    size: p.sizeUnits,
    side: p.side,
    fontSize: p.fontSize,
    layout: p.layout, // { lines, lineCount, lineHeightPx, ... }
    curve,
    sentence: p.sentence, // chosen external-text sentence (used to seed curated fill)
  };
}
